import React from 'react';
import styles from './Header.module.css';
import handsomeStyles from './HandsomeAnimation.module.css'; // Assuming this is needed for the brand
import { Menu, Plus, Bell } from 'lucide-react';
import Tooltip from './Tooltip';

type Theme = 'light' | 'dark' | 'auto';

interface HeaderProps {
  isAuthenticated: boolean;
  user: any; // Consider defining a more specific user type
  signOut?: () => void;
  theme: Theme;
  toggleTheme: () => void;
  onSignInClick: () => void;
  onJoinClick: () => void;
  getThemeIcon: () => React.ReactNode;
  getThemeLabel: () => string;
  onMenuClick?: () => void;
  onNewProjectClick?: () => void;
  isMenuOpen?: boolean;
  onMenuHoverEnter?: () => void;
  onMenuHoverLeave?: () => void;
  newInsightsCount?: number;
  onNotificationsClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  isAuthenticated,
  user,
  signOut,
  theme,
  toggleTheme,
  onSignInClick,
  onJoinClick,
  getThemeIcon,
  getThemeLabel,
  onMenuClick,
  onNewProjectClick,
  isMenuOpen = false,
  onMenuHoverEnter,
  onMenuHoverLeave,
  newInsightsCount = 0,
  onNotificationsClick,
}) => {
  if (isAuthenticated && user) {
    // Authenticated Navigation Header
    return (
      <nav className={styles.nav}>
        <div className={styles.navContainer}>
          <div className={styles.navLeft}>
            <Tooltip content="Toggle panel open" arrowAlign="start">
              <button 
                className={`${styles.menuButton} ${isMenuOpen ? styles.menuButtonActive : ''}`}
                onClick={onMenuClick}
                onMouseEnter={onMenuHoverEnter}
                onMouseLeave={onMenuHoverLeave}
                aria-label="Toggle menu"
              >
                <Menu size={20} />
              </button>
            </Tooltip>
            <Tooltip content="New resume project" offset={{ x: 60, y: 0 }} arrowAlign="start">
              <button 
                className={styles.newProjectButton}
                onClick={onNewProjectClick}
                aria-label="New resume project"
              >
                <Plus size={20} />
              </button>
            </Tooltip>
            <span className={styles.brandText}>
              <span className={handsomeStyles.handsomeAnimated}>handsome</span> CV
            </span>
          </div>
          
          <div className={styles.navUser}>
            <Tooltip content={`${newInsightsCount} new market insights`}>
              <button 
                onClick={onNotificationsClick}
                className={styles.notificationButton}
                aria-label="View notifications"
              >
                <Bell size={20} />
                {newInsightsCount > 0 && (
                  <span className={styles.notificationBadge}>
                    {newInsightsCount > 99 ? '99+' : newInsightsCount}
                  </span>
                )}
              </button>
            </Tooltip>
            <Tooltip content={`Switch to ${theme === 'light' ? 'dark' : theme === 'dark' ? 'auto' : 'light'} theme`}>
              <button 
                onClick={toggleTheme} 
                className={styles.themeToggleBtn}
                aria-label={`Switch to ${theme === 'light' ? 'dark' : theme === 'dark' ? 'auto' : 'light'} theme`}
              >
                {getThemeIcon()}
                <span className={styles.themeLabel}>{getThemeLabel()}</span>
              </button>
            </Tooltip>
            <div className={styles.userAvatar}>
              {user?.signInDetails?.loginId?.charAt(0).toUpperCase()}
            </div>
            <div className={styles.userDetails}>
              <span className={styles.userEmail}>{user?.signInDetails?.loginId}</span>
              {signOut && (
                <Tooltip content="Sign out of your account">
                  <button onClick={signOut} className={styles.signOutBtn}>
                    Sign out
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // Public Navigation Header
  return (
    <nav className={styles.nav}>
      <div className={styles.navContainer}>
        <div className={styles.navLeft}>
          <span className={styles.brandText}>
            <span className={handsomeStyles.handsomeAnimated}>handsome</span> CV
          </span>
        </div>
        
        <div className={styles.navActions}>
          <Tooltip content={`Switch to ${theme === 'light' ? 'dark' : theme === 'dark' ? 'auto' : 'light'} theme`}>
            <button 
              onClick={toggleTheme} 
              className={styles.themeToggleBtn}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : theme === 'dark' ? 'auto' : 'light'} theme`}
            >
              {getThemeIcon()}
              <span className={styles.themeLabel}>{getThemeLabel()}</span>
            </button>
          </Tooltip>
          <Tooltip content="Sign in to your account">
            <button onClick={onSignInClick} className={styles.signInBtn}>
              Sign In
            </button>
          </Tooltip>
          <Tooltip content="Start your free trial">
            <button onClick={onJoinClick} className={styles.joinBtn}>
              Try Free
            </button>
          </Tooltip>
        </div>
      </div>
    </nav>
  );
};

export default Header;
